import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FiPlus, FiEdit2, FiTrash2, FiBookOpen,
    FiMonitor, FiFileText, FiLink, FiCheck,
    FiX, FiSearch, FiLayers, FiDollarSign,
    FiClock, FiUser, FiMoreVertical, FiEye, FiSettings,
    FiActivity, FiZap, FiGrid, FiUnlock, FiChevronDown, FiExternalLink
} from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';
import toast from 'react-hot-toast';

const AdminStudyPacks = () => {
    const [packs, setPacks] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isPackModalOpen, setIsPackModalOpen] = useState(false);
    const [isContentModalOpen, setIsContentModalOpen] = useState(false);
    const [editingPack, setEditingPack] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const [packForm, setPackForm] = useState({
        teacher_id: '',
        title: '',
        description: '',
        subject: '',
        is_active: 1
    });

    const [contentList, setContentList] = useState([]);
    const [contentLoading, setContentLoading] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [packsData, teachersData] = await Promise.all([
                fetchAPI('/admin/get_study_packs.php'),
                fetchAPI('/admin/get_teachers.php')
            ]);
            setPacks(packsData);
            setTeachers(teachersData);
        } catch (error) {
            toast.error('Failed to load study pack data');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenPackModal = (pack = null) => {
        if (pack) {
            setEditingPack(pack);
            setPackForm({
                teacher_id: pack.teacher_id,
                title: pack.title,
                description: pack.description,
                subject: pack.subject,
                is_active: parseInt(pack.is_active)
            });
        } else {
            setEditingPack(null);
            setPackForm({
                teacher_id: teachers.length > 0 ? teachers[0].id : '',
                title: '',
                description: '',
                subject: '',
                is_active: 1
            });
        }
        setIsPackModalOpen(true);
    };

    const handleSavePack = async (e) => {
        e.preventDefault();
        try {
            const payload = editingPack ? { ...packForm, id: editingPack.id } : packForm;
            await fetchAPI('/admin/save_study_pack.php', {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            toast.success(editingPack ? 'Record updated' : 'Pack created');
            setIsPackModalOpen(false);
            loadData();
        } catch (error) {
            toast.error('Failed to save study pack');
        }
    };

    const handleDeletePack = async (id) => {
        if (!window.confirm('Are you sure you want to delete this study pack?')) return;
        try {
            await fetchAPI('/admin/delete_study_pack.php', {
                method: 'POST',
                body: JSON.stringify({ id })
            });
            toast.success('Deleted successfully');
            loadData();
        } catch (error) {
            toast.error('Failed to delete pack');
        }
    };

    const handleOpenContentModal = async (pack) => {
        setEditingPack(pack);
        setIsContentModalOpen(true);
        setContentLoading(true);
        try {
            const details = await fetchAPI(`/admin/get_study_pack_details.php?id=${pack.id}`);
            setContentList(details.contents || []);
        } catch (error) {
            toast.error('Failed to load pack content');
        } finally {
            setContentLoading(false);
        }
    };

    const handleAddContentItem = () => {
        setContentList([...contentList, { type: 'recording', title: '', url: '', is_free: 1 }]);
    };

    const handleRemoveContentItem = (index) => {
        setContentList(contentList.filter((_, i) => i !== index));
    };

    const handleContentChange = (index, field, value) => {
        const newList = [...contentList];
        newList[index][field] = value;
        setContentList(newList);
    };

    const handleSaveContent = async () => {
        try {
            await fetchAPI('/admin/save_pack_content.php', {
                method: 'POST',
                body: JSON.stringify({
                    pack_id: editingPack.id,
                    contents: contentList
                })
            });
            toast.success('Contents updated');
            setIsContentModalOpen(false);
            loadData();
        } catch (error) {
            toast.error('Failed to save content');
        }
    };

    const filteredPacks = packs.filter(p =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.teacher_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.subject?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 p-4 md:p-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8 bg-white p-8 rounded-xl shadow-sm border border-gray-100">
                <div>
                    <h1 className="text-2xl font-black text-gray-900 uppercase tracking-tight flex items-center gap-3">
                        <FiLayers className="text-blue-600" />
                        Management: <span className="text-blue-600">Study Packs</span>
                    </h1>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">Configure all free access modules</p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full md:w-auto">
                    <div className="relative group flex-1 sm:flex-none">
                        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                        <input
                            type="text"
                            placeholder="SEARCH REGISTRY..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="bg-gray-50 border border-gray-200 rounded-2xl py-3 pl-12 pr-4 text-xs font-bold text-gray-900 uppercase tracking-widest focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none w-full sm:w-64 transition-all"
                        />
                    </div>
                    <button
                        onClick={() => handleOpenPackModal()}
                        className="bg-blue-600 text-white px-8 py-3 rounded-2xl flex items-center justify-center gap-3 font-black uppercase tracking-widest transition-all hover:bg-blue-700 active:scale-95 shadow-lg shadow-blue-500/20"
                    >
                        <FiPlus size={20} /> New Pack
                    </button>
                </div>
            </div>

            {/* Registry Table */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50/50 border-b border-gray-100">
                                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Identification / Title</th>
                                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Teacher / Author</th>
                                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Payload</th>
                                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Operations</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-20 text-center">
                                        <div className="flex flex-col items-center gap-4">
                                            <div className="w-8 h-8 border-2 border-blue-600/10 border-t-blue-600 rounded-full animate-spin"></div>
                                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest animate-pulse">Accessing Core Registry...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredPacks.length > 0 ? (
                                filteredPacks.map((pack) => (
                                    <tr key={pack.id} className="hover:bg-gray-50/50 transition-colors group">
                                        <td className="px-6 py-5">
                                            <div className="flex items-start gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0 group-hover:scale-110 transition-transform">
                                                    <FiBookOpen size={18} />
                                                </div>
                                                <div>
                                                    <h3 className="text-sm font-bold text-gray-900 leading-tight uppercase tracking-tight">{pack.title}</h3>
                                                    <p className="text-[10px] font-bold text-gray-400 mt-1 uppercase tracking-widest">{pack.subject || 'UNSPECIFIED'}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                                                    <FiUser size={14} />
                                                </div>
                                                <span className="text-xs font-bold text-gray-600 uppercase tracking-tight">{pack.teacher_name || 'System Agent'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center justify-center gap-6">
                                                <div className="text-center group-hover:translate-y-[-2px] transition-transform">
                                                    <p className="text-xs font-black text-gray-900">{pack.recording_count || 0}</p>
                                                    <p className="text-[8px] font-black text-gray-400 uppercase">VIDS</p>
                                                </div>
                                                <div className="text-center group-hover:translate-y-[-2px] transition-transform delay-75">
                                                    <p className="text-xs font-black text-gray-900">{pack.pdf_count || 0}</p>
                                                    <p className="text-[8px] font-black text-gray-400 uppercase">PDFS</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${pack.is_active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                                                <div className={`w-1.5 h-1.5 rounded-full ${pack.is_active ? 'bg-green-500' : 'bg-red-500'} ${pack.is_active ? 'animate-pulse' : ''}`}></div>
                                                <span className="text-[10px] font-black uppercase tracking-widest">{pack.is_active ? 'Active' : 'Offline'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
                                                <button
                                                    onClick={() => handleOpenContentModal(pack)}
                                                    title="Manage Content"
                                                    className="w-9 h-9 flex items-center justify-center bg-gray-100 text-gray-500 hover:bg-blue-600 hover:text-white rounded-xl transition-all"
                                                >
                                                    <FiSettings size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleOpenPackModal(pack)}
                                                    title="Edit Structure"
                                                    className="w-9 h-9 flex items-center justify-center bg-gray-100 text-gray-500 hover:bg-gray-900 hover:text-white rounded-xl transition-all"
                                                >
                                                    <FiEdit2 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeletePack(pack.id)}
                                                    title="Delete Pack"
                                                    className="w-9 h-9 flex items-center justify-center bg-gray-100 text-gray-500 hover:bg-red-600 hover:text-white rounded-xl transition-all"
                                                >
                                                    <FiTrash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-32 text-center text-gray-300">
                                        <FiLayers size={48} className="mx-auto mb-4 opacity-20" />
                                        <p className="text-xs font-black uppercase tracking-[0.4em] opacity-40">Registry Empty</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modals Container */}
            <AnimatePresence>
                {/* Structure Modal */}
                {isPackModalOpen && (
                    <div className="fixed inset-0 z-[5000] flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 10 }}
                            className="bg-white w-full max-w-xl rounded-[2.5rem] shadow-2xl overflow-hidden"
                        >
                            <div className="p-10">
                                <h2 className="text-xl font-black text-gray-900 uppercase tracking-tight mb-8">
                                    {editingPack ? 'Update' : 'Register'} <span className="text-blue-600">Study Pack</span>
                                </h2>
                                <form onSubmit={handleSavePack} className="space-y-6">
                                    <div className="grid grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Author / Teacher</label>
                                            <select
                                                required
                                                value={packForm.teacher_id}
                                                onChange={(e) => setPackForm({ ...packForm, teacher_id: e.target.value })}
                                                className="w-full bg-gray-50 border border-transparent focus:bg-white focus:border-blue-500 rounded-2xl p-4 text-sm font-bold outline-none transition-all appearance-none"
                                            >
                                                <option value="">Select Author</option>
                                                {teachers.map(t => (
                                                    <option key={t.id} value={t.id}>{t.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Knowledge Category</label>
                                            <input
                                                required
                                                type="text"
                                                value={packForm.subject}
                                                onChange={(e) => setPackForm({ ...packForm, subject: e.target.value })}
                                                placeholder="e.g. CORE MATHS"
                                                className="w-full bg-gray-50 border border-transparent focus:bg-white focus:border-blue-500 rounded-2xl p-4 text-sm font-bold outline-none transition-all placeholder:text-gray-300"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Module Title</label>
                                        <input
                                            required
                                            type="text"
                                            value={packForm.title}
                                            onChange={(e) => setPackForm({ ...packForm, title: e.target.value })}
                                            placeholder="Descriptive Title..."
                                            className="w-full bg-gray-50 border border-transparent focus:bg-white focus:border-blue-500 rounded-2xl p-4 text-sm font-bold outline-none transition-all placeholder:text-gray-300"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Operational Description</label>
                                        <textarea
                                            rows={3}
                                            value={packForm.description}
                                            onChange={(e) => setPackForm({ ...packForm, description: e.target.value })}
                                            placeholder="Module scope and details..."
                                            className="w-full bg-gray-50 border border-transparent focus:bg-white focus:border-blue-500 rounded-2xl p-4 text-sm font-medium outline-none transition-all placeholder:text-gray-300 resize-none"
                                        ></textarea>
                                    </div>

                                    <div className="flex items-center gap-3 p-4 bg-green-50 rounded-2xl border border-green-100">
                                        <FiUnlock className="text-green-600" />
                                        <p className="text-[10px] font-black text-green-600 uppercase tracking-widest">Global Free Access Enabled</p>
                                    </div>

                                    <div className="flex gap-4 pt-4">
                                        <button
                                            type="button"
                                            onClick={() => setIsPackModalOpen(false)}
                                            className="flex-1 bg-gray-100 text-gray-500 font-black uppercase tracking-widest py-4 rounded-2xl hover:bg-gray-200 transition-all"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="flex-1 bg-blue-600 text-white font-black uppercase tracking-widest py-4 rounded-2xl shadow-lg shadow-blue-500/20 hover:bg-blue-700 transition-all"
                                        >
                                            Confirm
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </motion.div>
                    </div>
                )}

                {/* Content Modal */}
                {isContentModalOpen && (
                    <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsContentModalOpen(false)}
                            className="absolute inset-0 bg-gray-900/10 backdrop-blur-md"
                        ></motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="relative w-full max-w-5xl bg-white rounded-[2.5rem] shadow-[0_32px_64px_-12px_rgba(0,0,0,0.14)] overflow-hidden flex flex-col max-h-[90vh] border border-gray-100"
                        >
                            {/* Modal Header */}
                            <div className="px-10 py-8 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-white/50 backdrop-blur-md sticky top-0 z-20">
                                <div>
                                    <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tighter italic flex items-center gap-3">
                                        Payload <span className="text-blue-600">Registry</span>
                                        <div className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-black rounded-lg not-italic tracking-widest border border-blue-100/50">v2.0</div>
                                    </h2>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Configuring Vault: {editingPack?.title}</p>
                                </div>
                                <div className="flex items-center gap-3 w-full md:w-auto">
                                    <button
                                        onClick={handleAddContentItem}
                                        className="flex-1 md:flex-none bg-blue-600 text-white px-8 py-3.5 rounded-2xl flex items-center justify-center gap-3 text-[11px] font-black uppercase tracking-widest transition-all hover:bg-blue-700 shadow-xl shadow-blue-500/20 active:scale-95"
                                    >
                                        <FiPlus size={18} /> Inject Component
                                    </button>
                                    <button 
                                        onClick={() => setIsContentModalOpen(false)}
                                        className="w-12 h-12 flex items-center justify-center bg-gray-50 text-gray-400 hover:text-gray-900 rounded-2xl transition-all"
                                    >
                                        <FiX size={24} />
                                    </button>
                                </div>
                            </div>

                            <div className="p-10 flex-1 overflow-y-auto custom-scrollbar bg-gray-50/50">
                                {contentLoading ? (
                                    <div className="py-32 text-center">
                                        <div className="w-12 h-12 border-4 border-blue-600/10 border-t-blue-600 rounded-full animate-spin mx-auto mb-6"></div>
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.4em] animate-pulse">Scanning Data Fragments...</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 gap-4">
                                        {contentList.length === 0 ? (
                                            <div className="text-center py-24 border-2 border-dashed border-gray-200 rounded-[2rem] bg-white/50">
                                                <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6 text-gray-300">
                                                    <FiFileText size={40} />
                                                </div>
                                                <p className="text-xs font-black text-gray-400 uppercase tracking-[0.4em]">Registry Empty</p>
                                                <p className="text-[10px] font-bold text-gray-300 mt-2 uppercase tracking-widest">Deploy initial component to start</p>
                                            </div>
                                        ) : (
                                            contentList.map((item, idx) => (
                                                <motion.div
                                                    layout
                                                    key={idx}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all group/item relative overflow-hidden"
                                                >
                                                    <div className="flex flex-col xl:flex-row gap-6 items-start xl:items-end">
                                                        {/* Icon/Type Indicator */}
                                                        <div className="hidden xl:flex w-14 h-14 bg-gray-50 rounded-2xl items-center justify-center text-gray-400 group-hover/item:text-blue-600 group-hover/item:bg-blue-50 transition-colors flex-shrink-0 mb-1">
                                                            {item.type === 'recording' && <FiMonitor size={24} />}
                                                            {item.type === 'pdf' && <FiFileText size={24} />}
                                                            {item.type === 'live' && <FiExternalLink size={24} />}
                                                        </div>

                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 flex-1 w-full">
                                                            <div className="space-y-2">
                                                                <div className="flex justify-between items-center ml-1">
                                                                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Type</label>
                                                                    <span className="text-[8px] font-bold text-blue-500 uppercase tracking-tighter">Required</span>
                                                                </div>
                                                                <div className="relative">
                                                                    <select
                                                                        value={item.type}
                                                                        onChange={(e) => handleContentChange(idx, 'type', e.target.value)}
                                                                        className="w-full bg-gray-50/50 border border-gray-200 rounded-xl p-3.5 text-xs font-bold text-gray-900 outline-none focus:border-blue-500 transition-all appearance-none cursor-pointer"
                                                                    >
                                                                        <option value="recording">Video Recording</option>
                                                                        <option value="pdf">PDF Document</option>
                                                                        <option value="live">External Resource</option>
                                                                    </select>
                                                                    <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                                                </div>
                                                            </div>

                                                            <div className="space-y-2">
                                                                <div className="flex justify-between items-center ml-1">
                                                                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Component Name</label>
                                                                    <span className="text-[8px] font-bold text-gray-300 uppercase">Text</span>
                                                                </div>
                                                                <input
                                                                    type="text"
                                                                    value={item.title}
                                                                    onChange={(e) => handleContentChange(idx, 'title', e.target.value)}
                                                                    placeholder="e.g. Session Introduction"
                                                                    className="w-full bg-gray-50/50 border border-gray-200 rounded-xl p-3.5 text-xs font-bold text-gray-900 outline-none focus:border-blue-500 transition-all placeholder:text-gray-300"
                                                                />
                                                            </div>

                                                            <div className="space-y-2">
                                                                <div className="flex justify-between items-center ml-1">
                                                                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Resource Link</label>
                                                                    <span className="text-[8px] font-bold text-gray-300 uppercase">URL</span>
                                                                </div>
                                                                <div className="relative">
                                                                    <input
                                                                        type="text"
                                                                        value={item.url}
                                                                        onChange={(e) => handleContentChange(idx, 'url', e.target.value)}
                                                                        placeholder="https://..."
                                                                        className="w-full bg-gray-50/50 border border-gray-200 rounded-xl p-3.5 pr-10 text-xs font-bold text-gray-900 outline-none focus:border-blue-500 transition-all placeholder:text-gray-300"
                                                                    />
                                                                    <FiLink className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 group-hover/item:text-blue-400 transition-colors" />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center gap-3 pt-4 xl:pt-0 w-full xl:w-auto justify-end border-t xl:border-none border-gray-50">
                                                            <button
                                                                onClick={() => handleRemoveContentItem(idx)}
                                                                className="w-12 h-12 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-2xl transition-all"
                                                                title="Remove Component"
                                                            >
                                                                <FiTrash2 size={20} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="px-10 py-8 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row gap-4">
                                <button
                                    onClick={() => setIsContentModalOpen(false)}
                                    className="flex-1 bg-white border border-gray-200 text-gray-500 font-black uppercase tracking-widest py-4 rounded-2xl hover:bg-gray-100 transition-all text-xs"
                                >
                                    Dismiss Changes
                                </button>
                                <button
                                    onClick={handleSaveContent}
                                    className="flex-[2] bg-gray-900 text-white font-black uppercase tracking-widest py-4 rounded-2xl shadow-2xl shadow-gray-900/20 hover:bg-blue-600 transition-all text-xs flex items-center justify-center gap-3 group"
                                >
                                    Sync Data Registry <FiCheck className="group-hover:scale-125 transition-transform" />
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <style jsx>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #DFE3E8;
                    border-radius: 20px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #C4CDD5;
                }
            `}</style>
        </div>
    );
};

export default AdminStudyPacks;
